import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Bot, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { PremiumFeatureGate } from '@/components/premium/PremiumFeatureGate';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import { useTrialStatus } from '@/hooks/useTrialStatus';

interface AIResponse {
  message: string;
  studentName: string;
  beltLevel: string;
  fallbackMessage?: string;
}

const WarriorAI: React.FC = () => {
  useUiLanguage();
  const { profile } = useAuth();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation('common');
  const [message, setMessage] = useState('');
  const [conversation, setConversation] = useState<Array<{
    type: 'user' | 'ai';
    content: string;
    timestamp: Date;
  }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const sendToWarriorAI = async (userMessage: string) => {
    if (!profile?.id || !userMessage.trim()) return;

    setIsLoading(true);

    // Add user message to conversation
    const userEntry = {
      type: 'user' as const,
      content: userMessage,
      timestamp: new Date()
    };
    setConversation(prev => [...prev, userEntry]);

    try {
      console.log('Calling Warrior AI function with:', { userMessage, userId: profile.id, language: currentLanguage });

      const { data, error } = await supabase.functions.invoke('warrior-ai-assistant', {
        body: {
          message: userMessage,
          userId: profile.id,
          language: currentLanguage
        }
      });

      console.log('Warrior AI response:', { data, error });

      let aiMessage = t("common:messages.loading");

      if (data && !error) {
        const response: AIResponse = data;
        aiMessage = response.message || response.fallbackMessage || aiMessage;
      } else if (data && data.fallbackMessage) {
        aiMessage = data.fallbackMessage;
      }

      // Add AI response to conversation
      const aiEntry = {
        type: 'ai' as const,
        content: aiMessage,
        timestamp: new Date()
      };
      setConversation(prev => [...prev, aiEntry]);

    } catch (error) {
      console.error('Warrior AI Error:', error);

      const fallbackMessage = uiText("Osu! I'm experiencing technical difficulties, but remember: a true martial artist finds strength in adversity. Keep training!");

      const aiEntry = {
        type: 'ai' as const,
        content: fallbackMessage,
        timestamp: new Date()
      };
      setConversation(prev => [...prev, aiEntry]);

      toast({
        title: t("common:messages.error"),
        description: t("common:messages.error"),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || isLoading) return;

    const userMessage = message.trim();
    setMessage('');
    await sendToWarriorAI(userMessage);
  };

  const formatTime = (timestamp: Date) => {
    return timestamp.toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' });
  };

  const startConversation = () => {
    setIsExpanded(true);
    if (conversation.length === 0) {
      const greeting = currentLanguage === 'es' ? "¡Hola Warrior AI! Me gustaría recibir orientación sobre artes marciales." :
                      currentLanguage === 'fr' ? "Bonjour Warrior AI ! J'aimerais des conseils en arts martiaux." :
                      currentLanguage === 'de' ? "Hallo Warrior AI! Ich hätte gerne Kampfkunst-Beratung." :
                      currentLanguage === 'nl' ? "Hallo Warrior AI! Ik zou graag krijgskunst begeleiding willen." :
                      currentLanguage === 'el' ? "Γεια σας Warrior AI! Θα ήθελα καθοδήγηση για πολεμικές τέχνες." :
                      currentLanguage === 'pt' ? "Olá Warrior AI! Gostaria de orientação sobre artes marciais." :
                      "Hello Warrior AI! I'd like some martial arts guidance.";
      sendToWarriorAI(greeting);
    }
  };

  if (!profile) return null;

  const { hasActiveAccess } = useTrialStatus();

  return (
    <PremiumFeatureGate
      featureName={uiText("Warrior AI Assistant")}
      description={uiText("Get personalized martial arts guidance, technique explanations, and training advice from your AI instructor. Ask about belt requirements, training drills, and get expert martial arts coaching.")}
      customAccessCheck={hasActiveAccess}
    >
      <div className="w-full">
        {!isExpanded ? (
        <Card className="cursor-pointer hover:shadow-lg transition-all duration-200 border-2 border-dojo-primary/20 hover:border-dojo-primary/40">
          <CardHeader className="pb-3 px-4 sm:px-6" onClick={startConversation}>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Bot className="h-5 w-5 text-dojo-primary animate-pulse flex-shrink-0" />
              <span className="truncate">{t("common:warriorAI.title")}</span>
              <Badge variant="secondary" className="bg-dojo-primary text-white text-xs flex-shrink-0">
                {uiText("NEW")}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-4 sm:px-6" onClick={startConversation}>
            <div className="space-y-3">
              <p className="text-muted-foreground text-sm leading-relaxed">
                {t("common:warriorAI.description")}
              </p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                <span className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs text-muted-foreground">{t("common:warriorAI.categories.beltRequirements")}</span>
                <span className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs text-muted-foreground">{t("common:warriorAI.categories.techniqueTips")}</span>
                <span className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs text-muted-foreground">{t("common:warriorAI.categories.trainingDrills")}</span>
              </div>
              <Button className="w-full bg-dojo-primary hover:bg-dojo-primary-hover h-10 sm:h-11">
                <Bot className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="text-sm sm:text-base">{t("common:warriorAI.startTraining")}</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-2 border-dojo-primary/20">
          <CardHeader className="pb-3 bg-gradient-to-r from-dojo-primary/10 to-transparent">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Bot className="h-5 w-5 text-dojo-primary" />
                {t("common:warriorAI.title")}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(false)}
                className="h-8 w-8 p-0"
              >
                ×
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {/* Conversation Area */}
            <div className="h-80 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900/20">
              {conversation.length === 0 && !isLoading && (
                <div className="text-center py-8 text-muted-foreground">
                  <Bot className="h-12 w-12 mx-auto mb-3 text-dojo-primary opacity-50" />
                  <p className="font-medium">{t("common:warriorAI.ready")}</p>
                  <p className="text-sm mt-1">{t("common:warriorAI.askAbout")}</p>
                </div>
              )}

              {conversation.map((entry, index) => (
                <div key={index} className={`flex ${entry.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] ${
                    entry.type === 'user'
                      ? 'bg-dojo-primary text-white'
                      : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                  } rounded-lg p-3 shadow-sm`}>
                    {entry.type === 'ai' && (
                      <div className="flex items-center gap-2 mb-2">
                        <Bot className="h-4 w-4 text-dojo-primary" />
                        <span className="text-xs font-medium text-dojo-primary">{uiText("Warrior AI")}</span>
                      </div>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{entry.content}</p>
                    <span className={`text-xs mt-1 block ${
                      entry.type === 'user' ? 'text-white/70' : 'text-muted-foreground'
                    }`}>
                      {formatTime(entry.timestamp)}
                    </span>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-sm max-w-[80%]">
                    <div className="flex items-center gap-2 mb-2">
                      <Bot className="h-4 w-4 text-dojo-primary" />
                      <span className="text-xs font-medium text-dojo-primary">{uiText("Warrior AI")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-dojo-primary" />
                      <span className="text-sm text-muted-foreground">{t("common:warriorAI.thinking")}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input Area */}
            <div className="border-t p-4">
              <form onSubmit={handleSubmit} className="flex gap-2">
                <Input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t("common:warriorAI.placeholder")}
                  disabled={isLoading}
                  className="flex-1"
                />
                <Button
                  type="submit"
                  disabled={!message.trim() || isLoading}
                  className="bg-dojo-primary hover:bg-dojo-primary-hover"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>
        )}
      </div>
    </PremiumFeatureGate>
  );
};

export default WarriorAI;